import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import toast, { Toaster } from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { auth } from "../../helper/firebaseClient";
import { db } from "../../helper/firebaseClient";
import { useDepartment } from "../../stores/departmentStore";
import useAuth from "../../stores/authStore";
import { useDarkMode } from "../../context/darkModeContext";
import { DEPARTMENT_COLORS } from "../../styles/departmentColors";

const AddDept = ({ source }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { darkMode } = useDarkMode();
  const [deptName, setDeptName] = useState("");
  const [hex, setHex] = useState("#8B5CF6");
  const listDepartments = useDepartment((state) => state.listDepartments);
  const [loading, setLoading] = useState(false);
  const profileData = useAuth((state) => state.profileData);
  const departments = useDepartment((state) => state.departments);

  const usedColors = departments.map((d) => d?.metadata?.bg?.toUpperCase()).filter(Boolean);
  const availableColors = DEPARTMENT_COLORS.filter(
    (color) => !usedColors.includes(color.toUpperCase())
  );

  useEffect(() => {
    if (isOpen && availableColors.length > 0) {
      if (!availableColors.some(c => c.toUpperCase() === hex.toUpperCase())) {
        setHex(availableColors[0]);
      }
    }
  }, [isOpen, departments]);

  const openDialog = () => {
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setDeptName("");
    if (availableColors.length > 0) {
      setHex(availableColors[0]);
    }
  };

  const addDepartment = async () => {
    if (!deptName.trim()) return;

    setLoading(true);

    try {
      const token = await auth.currentUser.getIdToken();

      // Replace spaces with underscores in department name
      const formattedDeptName = deptName.replace(/ /g, "_");

      const orgId = profileData?.org_id || profileData?.org;

      const body = {
        name: formattedDeptName,
        idToken: token,
        org_id: orgId,
        metadata: {
          bg: hex,
          border: "#B7B6C2",
        },
      };

      const addDept = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/dept/createDepts`,
        body
      );

      console.log(addDept);

      await listDepartments();
      toast.success(`Department "${deptName.trim()}" created successfully`);

      closeDialog();

      // optional: reset fields
      setDeptName("");
      if (availableColors.length > 0) {
        setHex(availableColors[0]);
      }
    } catch (error) {
      console.log("error occurred while adding dept", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      {source === "settings" ? (
        <button
          onClick={openDialog}
          className="bg-[#5E5ADB] text-white text-sm rounded-lg py-2 px-3"
        >
          Add New Department
        </button>
      ) : (
        <button
          onClick={openDialog}
          title="create folder"
          className="text-2xl rounded-lg px-3 mx-4 text-center text-purple-600 border border-purple-600"
        >
          +
        </button>
      )}

      <Dialog
        open={isOpen}
        onClose={closeDialog}
        PaperProps={{
          style: {
            borderRadius: "12px",
            minWidth: "420px",
            maxWidth: "480px",
            backgroundColor: darkMode ? "#1E293B" : "#fff",
            color: darkMode ? "#E2E8F0" : "#111827",
          },
        }}
      >
        <DialogTitle
          className="text-xl font-semibold"
          sx={{ color: darkMode ? "#fff" : "#111827" }}
        >
          New Department
        </DialogTitle>
        <DialogContent
          style={{ backgroundColor: darkMode ? "#0F172A" : "#F7F8FA" }}
        >
          <div className="my-2 w-full py-2 space-y-4">
            <span className="w-full block">
              <p
                className={`mb-2 ${darkMode ? "text-slate-300" : "text-gray-700"}`}
              >
                Department Name <span className="text-red-500">*</span>
              </p>
              <input
                className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent ${darkMode ? "bg-[#1E293B] border-slate-600 text-white placeholder-slate-400" : "bg-white border-gray-300 text-gray-900"}`}
                type="text"
                placeholder="e.g. Engineering"
                value={deptName}
                onChange={(e) => setDeptName(e.target.value)}
              />
            </span>

            <div>
              <p
                className={`mb-3 ${darkMode ? "text-slate-300" : "text-gray-700"}`}
              >
                Color
              </p>
              {/* <div className="grid grid-cols-6 gap-2"> */}
              <div className="grid grid-cols-6 sm:grid-cols-7 gap-3">
                {availableColors.length > 0 ? (
                  availableColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setHex(color)}
                    className={`relative w-11 h-11 rounded-xl transition-all duration-200 hover:scale-110 hover:shadow-lg active:scale-95`}
                    style={{
                      backgroundColor: color,
                      border:
                        hex === color
                          ? "3px solid #111827"
                          : "2px solid rgba(255,255,255,0.9)",
                      boxShadow:
                        hex === color
                          ? "0 0 0 3px rgba(99,102,241,0.35)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    {hex === color && (
                      <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">
                        ✓
                      </span>
                    )}
                  </button>
                ))) : (
                  <p className={`col-span-7 text-sm ${darkMode ? "text-slate-400" : "text-gray-500"}`}>
                    All predefined colors are currently in use. Please delete a department or manually enter a color.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 mt-3">
                <span
                  className="w-8 h-8 rounded-full border border-gray-200 flex-shrink-0"
                  style={{ backgroundColor: hex }}
                />
                <p
                  className={`text-sm ${darkMode ? "text-slate-400" : "text-gray-600"}`}
                >
                  Selected: <span className="font-mono font-medium">{hex}</span>
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <button
            className={`px-4 py-2 rounded-lg border transition-colors ${darkMode ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-gray-300 hover:bg-gray-50"}`}
            onClick={closeDialog}
          >
            Cancel
          </button>
          <button
            className="flex gap-2 items-center px-4 py-2 rounded-lg bg-[#5E5ADB] text-white hover:bg-[#4C47C7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={addDepartment}
            disabled={loading || !deptName.trim()}
          >
            Add new department
            {loading && <CircularProgress size={16} color="inherit" />}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddDept;
